import { payerTypeConstants } from "constants/order";
import { isNoMaximumOOPDollars } from "helpers/price";
import useFormValidation from "hooks/useFormValidation";
import { Label } from "layout/typography/Label";
import { Checkbox } from "primereact/checkbox";
import { InputNumber } from "primereact/inputnumber";
import { Message } from "primereact/message";
import { Tooltip } from "primereact/tooltip";
import { useState, useMemo, useEffect } from "react";
import { Controller, FieldValues, useFormContext } from "react-hook-form";
import { useSelector } from "react-redux";
import styled from "styled-components";
import { IAccount } from "types/Account/Account";
import { IFacilityRules, INonFacilityRules } from "types/Order/Order";

import { buildNetworkStatusMessage } from "../../eligibilityMessages";
import { facilityBenefitsInfo, professionalBenefitsInfo } from "../../helpers";
import { MatchingOrders } from "./components/MatchingOrders";
import { PatientBenefits } from "./components/PatientBenefits";

const StyledInputNumber = styled(InputNumber)`
  input {
    max-width: 100%;
  }
`;

const StyledCheckboxContainer = styled.div`
  @media (min-width: 768px) {
    margin-top: 44px;
  }
`;

const StyledTooltip = styled(Tooltip)`
  .p-tooltip-text {
    background-color: white !important;
  }
`;

const StyledBenefitsContainer = styled.div`
  border-bottom: 1px solid var(--gray-new-200);
`;

const StyledBenefitsContainerLeft = styled.div`
  @media (min-width: 768px) {
    width: 60% !important;
  }
`;

const StyledBenefitsContainerRight = styled.div`
  @media (min-width: 768px) {
    width: 20% !important;
  }
`;

export function BenefitsSecondary({
  account,
  draft,
  redo,
  copy,
  costApplicationRules,
  eligibilityMessage,
}: {
  account: IAccount;
  draft?: boolean;
  redo?: boolean;
  copy?: boolean;
  costApplicationRules?: {
    facilityRules: IFacilityRules;
    nonFacilityRules: INonFacilityRules;
  };
  eligibilityMessage?: string | null;
}) {
  const [hideBenefits, setHideBenefits] = useState(false);
  const [showFacilityBenefits, setShowFacilityBenefits] = useState(
    account.costApplicationRulesVisible.facilityRules
  );
  const [showProfessionalBenefits, setShowProfessionalBenefits] = useState(
    account.costApplicationRulesVisible.nonFacilityRules
  );
  const { watch, control, trigger, setValue, getValues } =
    useFormContext<FieldValues>();

  // Sync local state with form state
  const formShowProfessionalCopay2 = watch("showProfessionalCopay2");
  const professionalFlatCopayValue2 = watch("professionalFlatCopay2");

  const [showProfessionalCopay, setShowProfessionalCopay] = useState(
    formShowProfessionalCopay2 ||
      (professionalFlatCopayValue2 && professionalFlatCopayValue2 > 0) ||
      false
  );

  useEffect(() => {
    if (
      formShowProfessionalCopay2 !== undefined ||
      (professionalFlatCopayValue2 && professionalFlatCopayValue2 > 0)
    ) {
      setShowProfessionalCopay(
        formShowProfessionalCopay2 ||
          (professionalFlatCopayValue2 && professionalFlatCopayValue2 > 0)
      );
      // Set default checkbox values when showing based on existing data
      if (
        !getValues("facilityProfessionalCopay2") &&
        !getValues("professionalProfessionalCopay2")
      ) {
        setValue("facilityProfessionalCopay2", false);
        setValue("professionalProfessionalCopay2", true);
      }
    }
  }, [
    formShowProfessionalCopay2,
    professionalFlatCopayValue2,
    setValue,
    getValues,
  ]);

  // Progressive deductible state
  const [reductionsApplied, setReductionsApplied] = useState(false);
  const [prevDeduct, setPrevDeduct] = useState<number>(0);
  const [prevOop, setPrevOop] = useState<number>(0);
  const benefitDetails2Field = watch("benefitDetails2");
  const payerName2Field = watch("payerName2");
  const copay2Field = watch("flatCopay2");
  const deductible2Field = watch("deductible2");
  const outOfPocketMax2Field = watch("outOfPocketMax2");
  const coinsurance2Field = watch("coinsurancePercent2");
  const professionalFlatCopay2Field = watch("professionalFlatCopay2");
  const draftOrderFirstRenderField = watch("draftOrderFirstRender");
  const redoOrderFirstRenderField = watch("redoOrderFirstRender");
  const copyOrderFirstRenderField = watch("copyOrderFirstRender");

  // Fields for reduction functionality
  const deductionSuggested = watch("deductibleReductionAmount") || 0;
  const oopSuggested = watch("oopReductionAmount") || 0;
  const hasMatchingOrders = watch("hasMatchingOrders") || false;
  const matchingOrdersData = watch("matchingOrdersData") || [];
  const outOfPocketMax2Value = watch("outOfPocketMax2");
  const isRemainingOutOfPocketMaxInfinity2 =
    watch("isRemainingOutOfPocketMaxInfinity2") ||
    isNoMaximumOOPDollars(outOfPocketMax2Value);

  const { handleOnBlurField } = useFormValidation();
  const { payersTypeInfo } = useSelector((state: any) => state.orderSlice);

  // Apply deductible and OOP reductions
  const applyReductions = () => {
    const currentDeductible2 = getValues("deductible2") || 0;
    const currentOop2 = getValues("outOfPocketMax2") || 0;
    // Store original values for potential undo
    setPrevDeduct(currentDeductible2);
    setPrevOop(currentOop2);

    // Apply reductions
    setValue(
      "deductible2",
      Math.max(0, currentDeductible2 - deductionSuggested)
    );
    setValue("outOfPocketMax2", Math.max(0, currentOop2 - oopSuggested));

    // Mark as applied
    setReductionsApplied(true);
    setValue("reductionsApplied2", true);

    // Validate fields after changes
    trigger(["deductible2", "outOfPocketMax2"]);
  };

  // Restore original values
  const undoReductions = () => {
    setValue("deductible2", prevDeduct);
    setValue("outOfPocketMax2", prevOop);
    setReductionsApplied(false);
    setValue("reductionsApplied2", false);
    trigger(["deductible2", "outOfPocketMax2"]);
  };

  // Reset reduction applied state when new suggestions arrive
  useEffect(() => {
    if (reductionsApplied && (deductionSuggested > 0 || oopSuggested > 0)) {
      setReductionsApplied(false);
      setValue("reductionsApplied2", false);
    }
  }, [deductionSuggested, oopSuggested]);

  useEffect(() => {
    const facilityRulesVisibleForAccount =
      account.costApplicationRulesVisible.facilityRules;
    const nonFacilityRulesVisibleForAccount =
      account.costApplicationRulesVisible.nonFacilityRules;
    const payerType = payersTypeInfo.find(
      (payer: { name: string; type: string }) => payer.name === payerName2Field
    )?.type;
    if (!payerType) return;
    let fieldValues = {
      facilityCopay2: true,
      facilityDeductible2: true,
      facilityCoinsurance2: true,
      professionalCopay2: true,
      professionalDeductible2: true,
      professionalCoinsurance2: true,
    };

    if (payerName2Field === "Self-Pay Rate") {
      setShowFacilityBenefits(false);
      setShowProfessionalBenefits(false);
      setHideBenefits(true);
    } else {
      setShowFacilityBenefits(facilityRulesVisibleForAccount);
      setShowProfessionalBenefits(nonFacilityRulesVisibleForAccount);
      setHideBenefits(false);
      if (
        ((draft && draftOrderFirstRenderField) ||
          (redo && redoOrderFirstRenderField) ||
          (copy && copyOrderFirstRenderField)) &&
        !!costApplicationRules
      ) {
        if (!facilityRulesVisibleForAccount) {
          setValue("facilityCopay2", true);
          setValue("facilityDeductible2", true);
          setValue("facilityCoinsurance2", true);
        }
        if (!nonFacilityRulesVisibleForAccount) {
          setValue("professionalCopay2", true);
          setValue("professionalDeductible2", true);
          setValue("professionalCoinsurance2", true);
        }
        return;
      }
      if (
        ((draft && draftOrderFirstRenderField) ||
          (redo && redoOrderFirstRenderField) ||
          (copy && copyOrderFirstRenderField)) &&
        !costApplicationRules
      ) {
        Object.entries(fieldValues).forEach(([field, value]) => {
          setValue(field, value);
        });
        return;
      }
      if (payerType === payerTypeConstants.MEDICARE_ADVANTAGE) {
        fieldValues = {
          ...fieldValues,
          facilityDeductible2: true,
          facilityCoinsurance2: facilityRulesVisibleForAccount
            ? !(copay2Field > 0)
            : true,
          professionalDeductible2: true,
          professionalCoinsurance2: nonFacilityRulesVisibleForAccount
            ? !(copay2Field > 0)
            : true,
        };
      }
    }

    Object.entries(fieldValues).forEach(([field, value]) => {
      setValue(field, value);
    });
  }, [payerName2Field, copay2Field, payersTypeInfo, draft, copy, redo]);

  // Handle professional copay automation from eligibility response
  useEffect(() => {
    const handleProfessionalCopayTrigger = (event: CustomEvent) => {
      // Show professional copay field if API returns a value > 0 AND nonFacilityRules is enabled
      if (
        event.detail?.value > 0 &&
        account.costApplicationRulesVisible.nonFacilityRules
      ) {
        setShowProfessionalCopay(true);
        setValue("showProfessionalCopay2", true);
        // Only uncheck professional copay if professional benefits are enabled
        if (showProfessionalBenefits) {
          setValue("professionalCopay2", false);
          trigger("professionalCopay2");
        }
        // Set default checkbox values for Professional Copay
        setValue("facilityProfessionalCopay2", false);
        setValue("professionalProfessionalCopay2", true);
        trigger("facilityProfessionalCopay2");
        trigger("professionalProfessionalCopay2");
      }
    };

    window.addEventListener(
      "triggerProfessionalCopay",
      handleProfessionalCopayTrigger as EventListener
    );
    return () => {
      window.removeEventListener(
        "triggerProfessionalCopay",
        handleProfessionalCopayTrigger as EventListener
      );
    };
  }, [showProfessionalBenefits, setValue, trigger, account]);

  const getReductionText = () => {
    if (deductionSuggested > 0 && oopSuggested > 0) {
      return (
        `Reduce deductible by $${deductionSuggested.toFixed(2)}` +
        ` and out-of-pocket max by $${oopSuggested.toFixed(2)}`
      );
    }
    return `Reduce deductible by $${deductionSuggested.toFixed(2)}`;
  };

  // The server corrects copays it knows the payer reports wrongly for this setting. It does
  // so on the secondary check too, so explain it here as well rather than silently
  // contradicting the payer (CLE-2540).
  // Medicaid payers have their benefits forced to $0, and as a secondary they absorb the whole
  // balance the primary leaves. Surface the same warning the primary section shows so staff know
  // the $0s are intentional and can override if wrong (CLE-2466, CLE-2605).
  const isMedicaidPayer2 = useMemo(() => {
    const payerType = payersTypeInfo.find(
      (payer: { name: string; type: string }) => payer.name === payerName2Field
    )?.type;
    return payerType === payerTypeConstants.MEDICAID;
  }, [payersTypeInfo, payerName2Field]);

  const flatCopayOverrideReason = useMemo(
    () => benefitDetails2Field?.response?.flatCopayOverride?.reason ?? null,
    [benefitDetails2Field]
  );

  // CLE-2683 — the account's benefit rules say it is out of network for this payer/plan. Rendered
  // in both the primary and secondary panels: the CLE-2540 review caught a correction that showed
  // in one and not the other, and a silent network-status change is the same class of problem.
  const networkStatusMessage = useMemo(
    () =>
      buildNetworkStatusMessage(
        benefitDetails2Field?.response?.networkStatusOverride
      ),
    [benefitDetails2Field]
  );

  const allDeductibleFieldsNull = useMemo(() => {
    if (!benefitDetails2Field) return false;
    const deductibleFieldIsNull =
      benefitDetails2Field?.response?.remainingDeductible === null;
    const familyCalendarYearDeductibleIsNull =
      benefitDetails2Field?.response?.familyCalendarYearDeductible === null;
    const individualCalendarYearDeductibleIsNull =
      benefitDetails2Field?.response?.individualCalendarYearDeductible === null;
    return (
      deductibleFieldIsNull &&
      familyCalendarYearDeductibleIsNull &&
      individualCalendarYearDeductibleIsNull
    );
  }, [benefitDetails2Field]);

  const coinsuranceFieldIsNull = useMemo(() => {
    if (!benefitDetails2Field) return false;
    return benefitDetails2Field?.response?.coinsurancePercent === null;
  }, [benefitDetails2Field]);

  const remainingDeductibleOptionCount = useMemo(() => {
    const otherOptions =
      benefitDetails2Field?.response?.details?.otherDeductibleOptions ?? [];
    if (otherOptions.length === 0) return 0;

    const distinctValues = new Set<number>();
    const selected = benefitDetails2Field?.response?.remainingDeductible;
    if (typeof selected === "number") distinctValues.add(selected);

    otherOptions.forEach((option: { Value?: string }) => {
      if (!option?.Value) return;
      const parsed = parseFloat(option.Value.replace(/[$,]/g, ""));
      if (!Number.isNaN(parsed)) distinctValues.add(parsed);
    });

    return distinctValues.size;
  }, [benefitDetails2Field]);

  // Get count of matching orders
  const orderCount = matchingOrdersData.length;

  if (hideBenefits) {
    return null;
  }
  return (
    <div className="w-full flex flex-column">
      <StyledBenefitsContainer className="w-full flex flex-column xl:flex-row gap-4 px-3">
        <div className="w-full flex-column md:flex-row xl:w-5 flex pt-5">
          <StyledBenefitsContainerLeft className="flex flex-column w-full  gap-3 mb-4">
            <div className="flex flex-column w-full gap-2">
              <div className="flex gap-2 w-full align-items-center">
                <Label htmlFor="deductible2">Remaining Deductible</Label>
                {allDeductibleFieldsNull && (
                  <>
                    <Tooltip
                      target=".info-icon"
                      style={{
                        fontSize: "14px",
                        width: "250px",
                      }}
                    />
                    <i
                      className="pi pi-info-circle info-icon"
                      data-pr-tooltip="Deductible info is missing. Please confirm with the payer that this plan has no deductible."
                      style={{ color: "var(--red-600)" }}
                    />
                  </>
                )}
              </div>

              <Controller
                name="deductible2"
                control={control}
                render={({ field: { onChange, onBlur, value } }) => (
                  <StyledInputNumber
                    value={value}
                    currency="USD"
                    locale="en-US"
                    mode="currency"
                    onBlur={() =>
                      handleOnBlurField({
                        onBlur,
                        field: "deductible2",
                        trigger,
                      })
                    }
                    inputId="deductible2"
                    onValueChange={({ value }) => {
                      onChange(value);
                      setValue(
                        "secondaryBenefitsUpdatedAt",
                        new Date().toISOString()
                      );
                    }}
                    minFractionDigits={2}
                    maxFractionDigits={2}
                    min={0}
                    aria-autocomplete="none"
                    className="w-full"
                  />
                )}
              />
              {remainingDeductibleOptionCount > 1 && (
                <Message
                  severity="warn"
                  className="mt-2"
                  text={`${remainingDeductibleOptionCount} remaining deductible values are available, please review.`}
                />
              )}
            </div>
            <div className="flex flex-column w-full gap-2">
              <Label htmlFor="outOfPocketMax2">
                Remaining Out-of-pocket Max
              </Label>

              <Controller
                name="outOfPocketMax2"
                control={control}
                render={({ field: { onChange, onBlur, value } }) => (
                  <StyledInputNumber
                    value={isRemainingOutOfPocketMaxInfinity2 ? null : value}
                    currency="USD"
                    locale="en-US"
                    mode="currency"
                    disabled={isRemainingOutOfPocketMaxInfinity2}
                    placeholder={
                      isRemainingOutOfPocketMaxInfinity2
                        ? "No maximum"
                        : undefined
                    }
                    onBlur={() =>
                      handleOnBlurField({
                        onBlur,
                        field: "outOfPocketMax2",
                        trigger,
                      })
                    }
                    inputId="outOfPocketMax2"
                    onValueChange={({ value }) => {
                      onChange(value);
                      setValue(
                        "secondaryBenefitsUpdatedAt",
                        new Date().toISOString()
                      );
                    }}
                    minFractionDigits={2}
                    maxFractionDigits={2}
                    min={0}
                    aria-autocomplete="none"
                  />
                )}
              />
            </div>
            <div className="flex flex-column w-full gap-2">
              <Label htmlFor="flatCopay2">Flat Copay</Label>
              <Controller
                name="flatCopay2"
                control={control}
                render={({ field: { onChange, onBlur, value } }) => (
                  <StyledInputNumber
                    value={value}
                    currency="USD"
                    locale="en-US"
                    mode="currency"
                    onBlur={() =>
                      handleOnBlurField({
                        onBlur,
                        field: "flatCopay2",
                        trigger,
                      })
                    }
                    inputId="flatCopay2"
                    onValueChange={({ value }) => {
                      if (redo) {
                        setValue("redoOrderFirstRender", false);
                      }
                      if (draft) {
                        setValue("draftOrderFirstRender", false);
                      }
                      if (copy) {
                        setValue("copyOrderFirstRender", false);
                      }
                      onChange(value);
                      setValue(
                        "secondaryBenefitsUpdatedAt",
                        new Date().toISOString()
                      );
                    }}
                    minFractionDigits={2}
                    maxFractionDigits={2}
                    min={0}
                    aria-autocomplete="none"
                    className="w-full"
                  />
                )}
              />
              {flatCopayOverrideReason && (
                <Message
                  severity="warn"
                  className="mt-2"
                  text={flatCopayOverrideReason}
                />
              )}
              {networkStatusMessage && (
                <Message
                  severity="warn"
                  className="mt-2"
                  text={networkStatusMessage}
                />
              )}
              {showProfessionalBenefits &&
                account.costApplicationRulesVisible.nonFacilityRules && (
                  <button
                    type="button"
                    className="text-sm text-primary-600 no-underline border-none bg-transparent cursor-pointer text-left"
                    onClick={(e) => {
                      e.preventDefault();
                      const newState = !showProfessionalCopay;
                      setShowProfessionalCopay(newState);
                      setValue("showProfessionalCopay2", newState);
                      // Auto-uncheck Prof checkbox only when showing (+Prof Copay)
                      if (newState && showProfessionalBenefits) {
                        setValue("professionalCopay2", false);
                        trigger("professionalCopay2");
                      }
                      // Set default checkbox values for Professional Copay when showing
                      if (newState) {
                        setValue("facilityProfessionalCopay2", false);
                        setValue("professionalProfessionalCopay2", true);
                        trigger("facilityProfessionalCopay2");
                        trigger("professionalProfessionalCopay2");
                      }
                    }}
                  >
                    {showProfessionalCopay ? "-Prof Copay" : "+Prof Copay"}
                  </button>
                )}
              {showProfessionalCopay ? (
                <div className="mt-2">
                  <Label htmlFor="professionalFlatCopay2">
                    Professional Copay
                  </Label>
                  <Controller
                    name="professionalFlatCopay2"
                    control={control}
                    render={({ field: { onChange, onBlur, value } }) => (
                      <StyledInputNumber
                        value={value}
                        currency="USD"
                        locale="en-US"
                        mode="currency"
                        onBlur={() =>
                          handleOnBlurField({
                            onBlur,
                            field: "professionalFlatCopay2",
                            trigger,
                          })
                        }
                        inputId="professionalFlatCopay2"
                        onValueChange={({ value }) => {
                          if (redo) {
                            setValue("redoOrderFirstRender", false);
                          }
                          if (draft) {
                            setValue("draftOrderFirstRender", false);
                          }
                          if (copy) {
                            setValue("copyOrderFirstRender", false);
                          }
                          onChange(value);
                          setValue(
                            "secondaryBenefitsUpdatedAt",
                            new Date().toISOString()
                          );
                        }}
                        minFractionDigits={2}
                        maxFractionDigits={2}
                        min={0}
                        aria-autocomplete="none"
                        className="w-full"
                      />
                    )}
                  />
                </div>
              ) : null}
            </div>
            <div className="flex flex-column w-full gap-2">
              <div className="flex gap-2 w-full align-items-center">
                <Label htmlFor="coinsurancePercent2">Coinsurance Percent</Label>
                {coinsuranceFieldIsNull && (
                  <>
                    <Tooltip
                      target=".info-icon"
                      style={{
                        fontSize: "14px",
                        width: "250px",
                      }}
                    />
                    <i
                      className="pi pi-info-circle info-icon"
                      data-pr-tooltip="Coinsurance info is missing. Please confirm with the payer that this plan has no coinsurance."
                      style={{ color: "var(--red-600)" }}
                    />
                  </>
                )}
              </div>

              <Controller
                name="coinsurancePercent2"
                control={control}
                render={({ field: { onChange, onBlur, value } }) => (
                  <StyledInputNumber
                    value={value}
                    suffix="%"
                    onBlur={() =>
                      handleOnBlurField({
                        onBlur,
                        field: "coinsurancePercent2",
                        trigger,
                      })
                    }
                    inputId="coinsurancePercent2"
                    onValueChange={({ value }) => {
                      onChange(value);
                      setValue(
                        "secondaryBenefitsUpdatedAt",
                        new Date().toISOString()
                      );
                    }}
                    min={0}
                    max={100}
                    aria-autocomplete="none"
                    className="w-full"
                  />
                )}
              />
              {isMedicaidPayer2 && (
                <Message
                  severity="warn"
                  className="mt-2"
                  text="Benefits are set to $0 because this payer is configured as Medicaid, and as the secondary it covers everything the primary leaves — so the patient owes $0. If this is incorrect, please update the benefits."
                />
              )}
            </div>
          </StyledBenefitsContainerLeft>
          {(showFacilityBenefits || showProfessionalBenefits) && (
            <StyledBenefitsContainerRight className="flex w-full flex-column md:flex-row gap-3">
              {showFacilityBenefits && (
                <div className="flex flex-column w-full align-items-center md:w-6">
                  <div className="flex gap-2">
                    <Label className="mb-2" htmlFor="facilityBenefits2">
                      Fac
                    </Label>
                    <StyledTooltip
                      target=".benefits-info"
                      style={{
                        fontSize: "14px",
                        width: "320px",
                      }}
                    >
                      <span className="text-gray-900">
                        {facilityBenefitsInfo.title}
                      </span>
                      <ul className="mt-2">
                        {facilityBenefitsInfo.items.map((item, index) => (
                          <li
                            className="flex gap-2 mb-2"
                            key={`${item.title}_${index}`}
                          >
                            <p className="font-normal italic text-gray-900">
                              <span className="font-bold text-gray-900">
                                {item.title}:
                              </span>{" "}
                              {item.info}
                            </p>
                          </li>
                        ))}
                      </ul>
                    </StyledTooltip>
                    <i
                      className="benefits-info pi pi-info-circle"
                      data-pr-tooltip=""
                      style={{
                        color: "var(--primary-color)",
                        marginTop: "2px",
                      }}
                    />
                  </div>
                  <div className="flex w-full pt-2 pb-3 justify-content-center ">
                    <Controller
                      name="facilityDeductible2"
                      control={control}
                      render={({ field: { onChange, value } }) => (
                        <div className="flex gap-2 align-items-center w-full justify-content-between md:justify-content-center">
                          <Label
                            htmlFor="facilityDeductible2"
                            className="flex md:hidden"
                          >
                            Deductible
                          </Label>
                          <Checkbox
                            className="ml-3"
                            onChange={onChange}
                            checked={value}
                            inputId="facilityDeductible2"
                            name="facilityDeductible2"
                          />
                        </div>
                      )}
                    />
                  </div>
                  <StyledCheckboxContainer className="flex w-full pt-2 pb-3 justify-content-center">
                    <div className="flex gap-2 align-items-center w-full justify-content-between md:justify-content-center">
                      <Label
                        htmlFor="facilityOutOfPocketMax2"
                        className="flex md:hidden"
                      >
                        Out-of-pocket Max
                      </Label>
                      <Checkbox
                        className="ml-3"
                        onChange={() => {}}
                        disabled
                        checked
                      />
                    </div>
                  </StyledCheckboxContainer>
                  <StyledCheckboxContainer className="flex w-full pt-2 pb-3 justify-content-center">
                    <Controller
                      name="facilityCopay2"
                      control={control}
                      render={({ field: { onChange, value } }) => (
                        <div className="flex gap-2 align-items-center w-full justify-content-between md:justify-content-center">
                          <Label
                            htmlFor="facilityCopay2"
                            className="flex md:hidden"
                          >
                            Copay
                          </Label>
                          <Checkbox
                            className="ml-3"
                            onChange={onChange}
                            checked={value}
                            inputId="facilityCopay2"
                            name="facilityCopay2"
                          />
                        </div>
                      )}
                    />
                  </StyledCheckboxContainer>
                  {showProfessionalCopay ? (
                    <StyledCheckboxContainer className="flex w-full pt-2 pb-3 justify-content-center">
                      <Controller
                        name="facilityProfessionalCopay2"
                        control={control}
                        render={({ field: { onChange, value } }) => (
                          <div className="flex gap-2 align-items-center w-full justify-content-between md:justify-content-center">
                            <Label
                              htmlFor="facilityProfessionalCopay2"
                              className="flex md:hidden"
                            >
                              Professional Copay
                            </Label>
                            <Checkbox
                              className="ml-3"
                              onChange={onChange}
                              checked={value}
                              inputId="facilityProfessionalCopay2"
                              name="facilityProfessionalCopay2"
                            />
                          </div>
                        )}
                      />
                    </StyledCheckboxContainer>
                  ) : null}
                  <StyledCheckboxContainer className="flex w-full pt-2 pb-3 justify-content-center">
                    <Controller
                      name="facilityCoinsurance2"
                      control={control}
                      render={({ field: { onChange, value } }) => (
                        <div className="flex gap-2 align-items-center w-full justify-content-between md:justify-content-center">
                          <Label
                            htmlFor="facilityCoinsurance2"
                            className="flex md:hidden"
                          >
                            Coinsurance
                          </Label>
                          <Checkbox
                            className="ml-3"
                            onChange={onChange}
                            checked={value}
                            inputId="facilityCoinsurance2"
                            name="facilityCoinsurance2"
                          />
                        </div>
                      )}
                    />
                  </StyledCheckboxContainer>
                </div>
              )}
              {showProfessionalBenefits && (
                <div className="flex flex-column w-full align-items-center md:w-6">
                  <div className="flex gap-2">
                    <Label className="mb-2" htmlFor="professionalBenefits2">
                      Prof
                    </Label>
                    <StyledTooltip
                      target=".prof-info"
                      style={{
                        fontSize: "14px",
                        width: "320px",
                      }}
                    >
                      <span className="text-gray-900 mb-2">
                        {professionalBenefitsInfo.title}
                      </span>
                      <ul className="mt-2">
                        {professionalBenefitsInfo.items.map((item, index) => (
                          <li
                            className="flex gap-2 mb-2"
                            key={`${item.title}_${index}`}
                          >
                            <p className="font-normal italic text-gray-900">
                              <span className="font-bold text-gray-900">
                                {item.title}:
                              </span>{" "}
                              {item.info}
                            </p>
                          </li>
                        ))}
                      </ul>
                    </StyledTooltip>
                    <i
                      className="prof-info pi pi-info-circle"
                      data-pr-tooltip=""
                      style={{
                        color: "var(--primary-color)",
                        marginTop: "2px",
                      }}
                    />
                  </div>
                  <div className="flex w-full pt-2 pb-3 justify-content-center">
                    <Controller
                      name="professionalDeductible2"
                      control={control}
                      render={({ field: { onChange, value } }) => (
                        <div className="flex gap-2 align-items-center w-full justify-content-between md:justify-content-center">
                          <Label
                            htmlFor="professionalDeductible2"
                            className="flex md:hidden"
                          >
                            Deductible
                          </Label>
                          <Checkbox
                            className="ml-3"
                            onChange={onChange}
                            checked={value}
                            inputId="professionalDeductible2"
                            name="professionalDeductible2"
                          />
                        </div>
                      )}
                    />
                  </div>
                  <StyledCheckboxContainer className="flex w-full pt-2 pb-3 justify-content-center">
                    <div className="flex gap-2 align-items-center w-full justify-content-between md:justify-content-center">
                      <Label
                        htmlFor="professionalOutOfPocketMax2"
                        className="flex md:hidden"
                      >
                        Out-of-pocket Max
                      </Label>
                      <Checkbox
                        className="ml-3"
                        onChange={() => {}}
                        disabled
                        checked
                      />
                    </div>
                  </StyledCheckboxContainer>
                  <StyledCheckboxContainer className="flex w-full pt-2 pb-3 justify-content-center">
                    <Controller
                      name="professionalCopay2"
                      control={control}
                      render={({ field: { onChange, value } }) => (
                        <div className="flex gap-2 align-items-center w-full justify-content-between md:justify-content-center">
                          <Label
                            htmlFor="professionalCopay2"
                            className="flex md:hidden"
                          >
                            Copay
                          </Label>
                          <Checkbox
                            className="ml-3"
                            onChange={onChange}
                            checked={value}
                            inputId="professionalCopay2"
                            name="professionalCopay2"
                          />
                        </div>
                      )}
                    />
                  </StyledCheckboxContainer>
                  {showProfessionalCopay ? (
                    <StyledCheckboxContainer className="flex w-full pt-2 pb-3 justify-content-center">
                      <Controller
                        name="professionalProfessionalCopay2"
                        control={control}
                        render={({ field: { onChange, value } }) => (
                          <div className="flex gap-2 align-items-center w-full justify-content-between md:justify-content-center">
                            <Label
                              htmlFor="professionalProfessionalCopay2"
                              className="flex md:hidden"
                            >
                              Professional Copay
                            </Label>
                            <Checkbox
                              className="ml-3"
                              onChange={onChange}
                              checked={value}
                              inputId="professionalProfessionalCopay2"
                              name="professionalProfessionalCopay2"
                            />
                          </div>
                        )}
                      />
                    </StyledCheckboxContainer>
                  ) : null}
                  <StyledCheckboxContainer className="flex w-full pt-2 pb-3 justify-content-center">
                    <Controller
                      name="professionalCoinsurance2"
                      control={control}
                      render={({ field: { onChange, value } }) => (
                        <div className="flex gap-2 align-items-center w-full justify-content-between md:justify-content-center">
                          <Label
                            htmlFor="professionalCoinsurance2"
                            className="flex md:hidden"
                          >
                            Coinsurance
                          </Label>
                          <Checkbox
                            className="ml-3"
                            onChange={onChange}
                            checked={value}
                            inputId="professionalCoinsurance2"
                            name="professionalCoinsurance2"
                          />
                        </div>
                      )}
                    />
                  </StyledCheckboxContainer>
                </div>
              )}
            </StyledBenefitsContainerRight>
          )}
        </div>
        <PatientBenefits
          benefitDetailsField={benefitDetails2Field}
          eligibilityMessage={eligibilityMessage}
          deductibleField={deductible2Field}
          outOfPocketMaxField={outOfPocketMax2Field}
          payerType={
            payersTypeInfo.find(
              (payer: { name: string; type: string }) =>
                payer.name === payerName2Field
            )?.type
          }
          handleChangeDeductibleField={(value: number) => {
            setValue("deductible2", value);
            setValue("secondaryBenefitsUpdatedAt", new Date().toISOString());
          }}
          handleChangeOOPField={(value: number) => {
            setValue("outOfPocketMax2", value);
            setValue("secondaryBenefitsUpdatedAt", new Date().toISOString());
          }}
          coinsuranceField={coinsurance2Field}
          handleChangeCoinsuranceField={(value: number) => {
            setValue("coinsurancePercent2", value);
            setValue("secondaryBenefitsUpdatedAt", new Date().toISOString());
          }}
        />
      </StyledBenefitsContainer>
      {hasMatchingOrders && (
        <MatchingOrders
          items={matchingOrdersData}
          severity={reductionsApplied ? "success" : "warn"}
          textTop={
            reductionsApplied
              ? `Successfully applied reductions to benefits`
              : `Found ${orderCount} previous order${
                  orderCount !== 1 ? "s" : ""
                } for this patient.`
          }
          textBottom={reductionsApplied ? `` : `${getReductionText()}`}
          buttonLabel={reductionsApplied ? "Undo" : "Apply Reductions"}
          onButtonClick={reductionsApplied ? undoReductions : applyReductions}
          buttonDisabled={
            !reductionsApplied &&
            (deductible2Field == null || deductible2Field === "") &&
            (outOfPocketMax2Field == null || outOfPocketMax2Field === "")
          }
          buttonTooltip="Enter the deductible or out-of-pocket max first"
        />
      )}
    </div>
  );
}
